import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { TrendingUpIcon, CoinsIcon, PercentIcon, CheckCircle2Icon } from 'lucide-react';
function AnimatedCounter({
  target,
  suffix = '',
  duration = 2
}: {
  target: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true
  });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = target / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref} data-id="element-77">
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export function FinancialAnalysis() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const financialData = [{
    label: 'Chirie medie',
    value: '12 €/mp + TVA',
    icon: CoinsIcon
  }, {
    label: 'Venit anual estimat',
    value: '≈ 300.000 €',
    icon: TrendingUpIcon,
    animated: true,
    animatedValue: 300
  }, {
    label: 'Randament minim',
    value: '8–9%',
    icon: PercentIcon,
    animated: true,
    animatedValue: 8
  }, {
    label: 'Creștere potențială valoare',
    value: '30–35%',
    icon: TrendingUpIcon,
    animated: true,
    animatedValue: 30
  }, {
    label: 'Grad de ocupare',
    value: '85%',
    icon: PercentIcon,
    animated: true,
    animatedValue: 85
  }, {
    label: 'Eligibilitate finanțare',
    value: 'Imobil finanțabil',
    icon: CheckCircle2Icon
  }];
  return <section className="py-12 md:py-20 px-4 md:px-6 lg:px-16 bg-white" data-id="element-78">
      <div className="max-w-7xl mx-auto" ref={ref} data-id="element-79">
        <motion.h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-[#132c53] text-center mb-3 md:mb-4 px-4" initial={{
        y: -50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-80">
          Analiză <span className="text-[#357beb]" data-id="element-81">Financiară</span>
        </motion.h2>
        <motion.p className="text-gray-700 text-center mb-8 md:mb-12 max-w-3xl mx-auto text-base md:text-lg px-4" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-82">
          Imobilul generează venituri imediate și oferă opțiunea unei
          reconversii cu profit accelerat datorită cererii ridicate pentru
          locuințe urbane în zona Unirii.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-83">
          {financialData.map((item, index) => <motion.div key={index} className="bg-gray-50 border border-gray-200 p-4 md:p-6 rounded-xl hover:shadow-lg transition-all duration-300" initial={{
          y: 50,
          opacity: 0
        }} animate={inView ? {
          y: 0,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} data-id="element-84">
              <div className="flex items-start gap-3 md:gap-4" data-id="element-85">
                <div className="p-2 md:p-3 bg-[#357beb]/10 rounded-lg flex-shrink-0" data-id="element-86">
                  <item.icon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-87" />
                </div>
                <div data-id="element-88">
                  <p className="text-gray-600 text-xs md:text-sm mb-1" data-id="element-89">
                    {item.label}
                  </p>
                  <p className="text-[#132c53] text-xl md:text-2xl font-bold" data-id="element-90">
                    {item.animated && item.animatedValue ? <>
                        <AnimatedCounter target={item.animatedValue} suffix={item.label.includes('%') ? '%' : item.label.includes('Venit') ? '.000 Euro' : ''} data-id="element-91" />
                        {item.label.includes('Randament') ? '–9%' : item.label.includes('Creștere') ? '–35%' : ''}
                      </> : item.value}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}